// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Loading from 'react-loading';
import { toast } from 'react-toastify';

import api from '../../api';
import ContextMenu from '../ContextMenu';

import plural from '../../utilities/plural';
import onPages from '../../utilities/onPage';
import { decodeURL } from '../../utilities/url';
import formatDate from '../../utilities/formatDate';
import getSourceIcon from '../../utilities/getSourceIcon';

const onPage = (url) => onPages([ url ]);

let resultLimit = 100;

export default class Documents extends React.Component {
    abortController = new AbortController();
    state = {

        resultOffset: 0,
        loadingMore: false,
        dragOver: false,

        sortBy: "",
        sortDir: 1, // 1: Ascending, -1, Descending
        cmRoot: false,

        reachedResultLimit: false,
        isDocumentContextMenuOpen : false,
        
        loadingDocuments: false,
    }

    loading = false
    filter = ""

    componentDidUpdate = async (prev) => {
        if (this.props.scrollPosition !== prev.scrollPosition && this.props.scrollPosition === "bottom" && this.state.reachedResultLimit)
            this.loadMoreDocuments(this.props.match.params.stackId)
    }

    componentDidMount = async () => {
        document.addEventListener('keydown', this.handleSelectAll);

        if (this.props.match.params.stackId)
            return await this.loadDocuments(this.props.match.params.stackId);

        // Check url for type of page to load
        if (onPage("/archives"))
            return await this.loadArchives();
            
        if (onPage("/field/"))
            return await this.loadFieldDocs(this.props.match.params.field, this.props.match.params.value);

        if (onPage("/trash"))
            return await this.loadTrash();
    }

    componentWillReceiveProps = async (next) => {
        const filter = this.makeFilterQuery(([ ...next.filter ] ?? []).filter(f => f.key?.length && f.value?.length));
        const filterChanged = filter !== this.filter;

        this.filter = filter;

        if (next.match.params.stackId && (next.match.params.stackId !== this.props.match.params.stackId || filterChanged))
            return await this.loadDocuments(next.match.params.stackId, filter);

        if (onPage("/archives") && (!this.props.stack || this.props.stack._id !== "archives"))
            return await this.loadArchives();
        
        if (onPage("/trash") && (!this.props.stack || this.props.stack._id !== "trash"))
            return await this.loadTrash();

        if (next.match.params.field !== this.props.match.params.field || next.match.params.value !== this.props.match.params.value)
            return await this.loadFieldDocs(next.match.params.field, next.match.params.value);
    }

    componentWillUnmount = () => {
        document.removeEventListener('keydown', this.handleSelectAll);
        this.abortController.abort();
        this.props.loaded();
    }

    resetState = async () => await this.setState({
        resultOffset: 0,
        loadingMore: false,
        sortBy: "",
        sortDir: 1,
        cmRoot: false,
        reachedResultLimit: false
    })

    handleSelectAll = () => {
        document.addEventListener('keydown', (e) => {
            if(e.ctrlKey && e.key === 'a' && !onPage('/document')){
                this.props.select(this.props.docs ? this.props.docs.map(d => d._id) : [])
            }
        })
    }

    makeFilterQuery = (filter) => {
        let filterStr = "";
        for (const f of filter)
            filterStr += "&m::" + encodeURIComponent(f.key) + "=" + encodeURIComponent(f.value);
        return filterStr;
    }

    loadFieldDocs = async (field, value) => {
        if (!field || !value)
            return;

        field = decodeURL(field);
        value = decodeURL(value);

        await this.props.loading();
        this.props.alert("");

        await this.props.setFieldValue(field, value);
        await this.resetState();

        const res = await api.get('/field/docs?field=' + encodeURIComponent(field) + '&value=' + encodeURIComponent(value) + `&resultOffset=${this.state.resultOffset}&resultLimit=${resultLimit}`);
        if(res.docs.length >= resultLimit) {
            this.setState({
                reachedResultLimit: true,
            });
        }

        if (res && res.meta && res.docs) {
            await this.props.setStack(res.meta);
            await this.props.setDocs(res.docs);
        } else {
            await this.props.setStack({ _id: null, path: "" });
            await this.props.setdocs([]);
        }

        this.props.loaded();
    }

    loadTrash = async () => {
        if (this.loading)
            return;

        this.loading = true;

        await this.props.setStack({
            _id: "trash",
            path: "Deleted Documents"
        });
        
        await this.props.loading();
        this.props.alert("");
        await this.resetState();

        const res = await api.get('/stack/trash?resultOffset=0&resultLimit=' + resultLimit);

        if (res && res.docs && res.nDeleted) {
            await this.props.setDocs(res.docs);
            await this.props.setNDeleted(res.nDeleted);
            if (res.docs.length >= resultLimit) {
                this.setState({
                    reachedResultLimit: true,
                });
            }
        } else {
            await this.props.setDocs([]);
            await this.props.setNDeleted(0)
        }

        this.props.loaded();
        this.loading = false;
    }

    loadArchives = async () => {
        if (this.state.loadingDocuments)
            return;

        this.setState({loadingDocuments: true})

        await this.props.setStack({
            _id: "archives",
            path: "Archived Documents"
        });
        
        this.props.alert("");
        await this.resetState();

        const res = await api.get('/stack/archive?resultOffset=0&resultLimit=' + resultLimit);

        if (res && res.docs && res.nArchived) {
            await this.props.setDocs(res.docs);
            await this.props.setNArchived(res.nArchived);
        } else {
            await this.props.setDocs([]);
            await this.props.setNArchived(0);
        }
        this.setState({loadingDocuments: false})
    }

    loadDocuments = async (stackId, filter="") => {

        this.setState({loadingDocuments: true})
        this.props.alert("");
        await this.resetState();

        // Setup a new abort controller to handle switching stacks quickly 
        this.abortController.abort() 
        this.abortController = new AbortController() 

        // Find the stack 
        const stack = this.props.stacks.find(s => s._id === stackId);
        if(stack) 
            this.props.setStack(stack, stack.keys??[]);

        const res = await api.get('/stack/list?stackId=' + stackId + '&resultOffset=0&resultLimit=' + resultLimit + filter, {signal: this.abortController.signal});

        if (res && res.stack && res.docs) {
            const keys = res.stack?.keys || [];
            if (res.fields)
                for (const key of res.fields)
                    if (!keys.includes(key))
                        keys.push(key);

            await this.props.setStack({
                ...res.stack,
                keys
            });

            const deDuplicated = []; 
            const seen = new Set();
            res.docs.forEach(doc => {
                if(!seen.has(doc._id)){
                    deDuplicated.push(doc);
                    seen.add(doc._id);
                }
            });

        
            await this.props.setDocs(deDuplicated);
            this.setState({loadingDocuments: false});

            // Will show our load more button
            if (res.docs.length >= resultLimit) {
                this.setState({
                    reachedResultLimit: true,
                });
            }

        } else if(!res?.cancelled){
            await this.props.setStack({ _id: stackId, path: "" });
            this.setState({loadingDocuments: false})
            await this.props.setDocs([]);
        }

        this.props.loaded();
    }

    loadMoreDocuments = async (stackId) => {

        // Stop propagation of the mouse event, or it may pull up the upload window when clicking "load more"
        // after moving all documents out
        //mouseEvent.stopPropagation();

        await this.setState({
            loadingMore: true,
            resultOffset: this.props.docs.length,
            reachedResultLimit: false,
        });

        let url;
        if (onPage('/field/'))
            url = '/field/docs?field=' + this.props.match.params.field + '&value=' + this.props.match.params.value + '&';

        else if (onPage("archives"))
            url = '/stack/archive?';

        else if(onPage("trash"))
            url = '/stack/trash?'

        else url = '/stack/list?stackId=' + stackId + '&';


        url += 'resultOffset=' + this.state.resultOffset + '&resultLimit=' + resultLimit;

        if (this.state.sortBy !== "")
            url += "&sort=" + this.state.sortBy + "&dir=" + this.state.sortDir;

        url += this.filter;

        const res = await api.get(url);

        if (res && res.docs) {
            const docs = [...this.props.docs, ...res.docs]
            const deDuplicated = []; 
            const seen = new Set();
            docs.forEach(doc => {
                if(!seen.has(doc._id)){
                    deDuplicated.push(doc);
                    seen.add(doc._id);
                }
            });

            await this.props.setDocs(docs);

            // Set the option to load even more
            if (res.docs.length >= resultLimit) {
                this.setState({
                    reachedResultLimit: true,
                });
            }
        }

        await this.setState({ loadingMore: false });

    }

    sort = () => {
        let { sortBy, sortDir } = this.state;

        if (sortBy === "")
            return this.props.docs;

        let sorted = this.props.docs.slice();
        sorted.sort((a,b) => a[sortBy] > b[sortBy] ? sortDir : a[sortBy] < b[sortBy] ? -sortDir : 0);
        return sorted;
    }

    toggleHeader = (key) => async () => {
        const sortBy = key;
        const sortDir = this.state.sortBy === key ? (this.state.sortDir > 0 ? -1 : 1) : -1;

        // If there are more files to load, then we need to perform a DB query
        if (this.props.docs.length === this.state.resultOffset + resultLimit) {
            this.setState({ sortBy, sortDir, resultOffset: 0 });
            await this.props.loading();

            let url;
            if (onPage("/archives")) {
                url = '/stack/archive?';
            } else if (onPage("/trash")) {
                url = '/stack/trash?';
            } else if (onPage("/field/") && this.props.match.params.field && this.props.match.params.value) {
                const field = decodeURL(this.props.match.params.field);
                const value = decodeURL(this.props.match.params.value);
                url = '/field/docs?field=' + encodeURIComponent(field) +
                    '&value=' + encodeURIComponent(value) + '&';
            } else {
                url = '/stack/list?stackId=' + this.props.match.params.stackId + '&';
            }
            const res = await api.get(url + 'resultOffset=0&resultLimit=' + resultLimit + 
                '&sort=' + sortBy + '&dir=' + sortDir + this.filter);

            if (res && res.docs) {
                const deDuplicate = [...new Set([...res.docs])];
                await this.props.setDocs(deDuplicate);
            }

            return this.props.loaded();
        }

        this.setState({ sortBy, sortDir });
    }

    getSortArrow = (key) => {
        if (this.state.sortBy !== key)
            return "";

        return this.state.sortDir > 0 ? <>▴&nbsp;</> : <>▾&nbsp;</>;
    }

    getContextMenuOptions = () => {

        let options = [];

        // Root context menu
        if (this.state.cmRoot) {

            if (!onPage("/archives") && !onPage("/trash") && this.props.stack && this.props.stack._id)
                options.push(
                    {
                        label: "Create a new stack",
                        action: () => this.props.createStack(this.props.stack)
                    },
                    {
                        label: 'Upload file(s) here',
                        action: this.props.uploadFiles
                    }
                );

            if (onPage("/field/") && this.props.match.params.field && this.props.match.params.value)
                options.push({
                    label: 'Upload file(s) here',
                    action: () => this.props.uploadFilesDirect(null, [{
                        key: this.props.match.params.field,
                        value: this.props.match.params.value
                    }])
                });

                options.push(
                    {
                        label: 'Select all visible',
                        action: () => {
                            this.props.select(this.props.docs ? this.props.docs.map(d => d._id) : [])
                        }
                    },
                    {
                        label: 'Select all',
                        action: async () => {
                            const resp = await api.get('/stack/docs?stackId=' + this.props.match.params.stackId);
                            this.props.select(this.props.docs ? resp.docs : [])
                        }
                    },
                    {
                        label: 'Unselect all',
                        action: () => this.props.select([])
                    }
                )
        }

        // Document context menu
        else if (this.props.selection.length > 1 && !onPage('/trash')) {
            options.push(
                {
                    label: 'Mark all as urgent',
                    action: () => this.props.updateDocuments(this.props.selection, { urgent: true })
                },
                {
                    label: 'Mark all not urgent',
                    action: () => this.props.updateDocuments(this.props.selection, { urgent: false })
                },
                {
                    label: 'Mark all as read',
                    action: () => this.props.updateDocuments(this.props.selection, { reviewed: true })
                },
                {
                    label: 'Mark all not read',
                    action: () => this.props.updateDocuments(this.props.selection, { reviewed: false })
                },
                {
                    label: 'Archive all',
                    action: () => this.props.archiveDocuments(this.props.selection)
                },
                {
                    label: 'Unarchive all',
                    action: () => this.props.archiveDocuments(this.props.selection, false)
                },
                {
                    label: 'Duplicate all',
                    action: () => this.props.copyDocuments(this.props.selection)
                },
                {
                    label: 'Copy to another stack',
                    action: () => this.props.copyDocumentsTo(this.props.selection)
                },
                {
                    label: 'Move all',
                    action: () => this.props.moveDocuments(this.props.selection)
                },
                {
                    label: 'Delete all',
                    action: () => this.props.modal("confirm", {
                        message: <>You are about to delete <strong> {this.props.selection.length} </strong> selected documents. All other team users will lose access to these documents as well. To restore the documents, go to the <a href="/trash">Trash</a>.</>,
                        action: async () => await this.props.removeDocuments(this.props.selection)
                    }) 
                },
            );

            // Admin options
            if (this.props.user?.email?.includes("@stax.ai") || this.props.team?.scopes?.includes("ADMIN")) {
                options.push(
                    {
                        label: 'Retry failed jobs',
                        action: () => this.props.retryFailedJobs(this.props.selection, this.props.stack._id)
                    },
                    {
                        label: 'Re-sort all',
                        action: () => this.props.resortDocuments(this.props.selection)
                    },
                    {
                        label: 'Rerun pipeline',
                        action: () => this.props.rerunPipeline(this.props.selection)
                    },
                    {
                        label: 'Re-process all',
                        action: () => this.props.reprocessDocuments(this.props.selection)
                    }
                );
            }
            
        }

        else if(!onPage('/trash')){
        
            options.push(
                {
                    label: 'Open in new tab',
                    action: (doc) => window.open('/stack/' + (this.props.match.params.stackId ? this.props.match.params.stackId : doc.stack ? doc.stack : "unclassified") + '/document/' + doc._id, '_blank')
                },
                {
                    label: (doc) => doc && doc.urgent ? 'Not urgent' : 'Mark as urgent',
                    action: (doc) => this.props.updateDocument(doc._id, { urgent: !doc.urgent })
                },
                {
                    label: (doc) => doc && doc.reviewed ? 'Mark unread' : 'Mark as read',
                    action: (doc) => this.props.updateDocument(doc._id, { reviewed: !doc.reviewed })
                },
                {
                    label: 'Download',
                    action: (doc) => this.props.downloadDocument(doc)
                },
                {
                    label: 'Print',
                    action: (doc) => this.props.printDocument(doc)
                },
                {
                    label: 'Share',
                    action: (doc) => this.props.sendDocument(doc)
                },
                {
                    label: 'Rename',
                    action: (doc) => this.props.renameDocument(doc)
                },
                {
                    label: 'Duplicate',
                    action: (doc) => this.props.copyDocuments([doc._id])
                },
                {
                    label: 'Copy',
                    action: (doc) => this.props.copyDocumentsTo([doc._id])
                },
                {
                    label: 'Move',
                    action: (doc) => this.props.moveDocuments([doc._id])
                },
                {
                    label: (doc) => doc && doc.archived ? 'Unarchive' :  'Archive',
                    action: (doc) => this.props.archiveDocuments([doc._id], !doc.archived)
                },
                {
                    label: 'Delete',
                    action: (doc) => this.props.modal("confirm", {
                        message: <>You are about to delete this document. All other team users will lose access to the document as well. To restore this document, go to <a href="/trash">Trash</a>.</>,
                        action: async () => await this.props.removeDocuments([doc._id])
                    })
                },
                {
                    label: (doc) => doc && doc.pinned ? 'Unpin' : 'Pin to top',
                    action: (doc) => this.props.updateDocument(doc._id, { pinned: !doc.pinned })
                },
            );

            // Admin options

            if (this.props.user?.email?.includes("@stax.ai") || this.props.team?.scopes?.includes("ADMIN")) {
                options.push(
                    {
                        label: 'Retry failed job',
                        action: (doc) => this.props.retryFailedJobs([doc._id], this.props.stack._id)
                    },
                    {
                        label: 'Re-sort',
                        action: (doc) => this.props.resortDocuments([doc._id])
                    },
                    {
                        label: 'Re-process',
                        action: (doc) => this.props.reprocessDocuments([ doc._id ])
                    },
                    {
                        label: 'Rerun pipeline',
                        action: (doc) => this.props.rerunPipeline(doc._id)
                    }
                );
            }

            // Generic options for hte bottom
            options.push({
                label: 'Report error',
                action: (doc) => this.props.modal('report-document', doc._id)
            });
                
        }

        else if(onPage('/trash')){
            options.push(
                {
                    label: plural('Restore document', this.props.selection?.length > 1? 2:1),
                    action: (doc) => {
                        this.props.restoreDocuments(this.props.selection.length > 0? this.props.selection: [doc._id])
                        this.props.setNDeleted(this.props.nDeleted - Math.max(this.props.selection.length,1))
                    }
                }
            )
        }

        return options;

    }

    renderJobStatus = (doc) => {
        // let timedOut;
        // try {
        //     timedOut = (new Date() - new Date(Date.parse(doc.receivedOn))) / 60000 > 60;
        // } catch (error) {
        //     timedOut = true; // Don't know what happened...
        // }
        // Get the recieved on date of the doc....  
        const receivedOn = doc.receivedOn ? new Date(doc.receivedOn) : null;  
        const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000);
        
        if (!doc.job && receivedOn < tenMinutesAgo)
            return <></>;

        if (doc.job.error)
            return <img
                className="document-thumbnail-icon"
                src="https://static.stax.ai/images/icons/error.png"
                alt="Error"
            />;
        
        if(!doc.job || doc?.job?.working || (!doc.pages?.length && receivedOn > tenMinutesAgo))
            return <div className="document-thumbnail-icon">
                <Loading
                    type="spin"
                    color="var(--color-primary)"
                    height={32}
                    width={32}
                />
            </div>;  
    }

    renderAssignStatus = (doc) => {
        let userName = (this.props.team?.users.find(user=>user._id === doc.assignee))?.name

        if (doc.assignee)
            return <div className="document-thumbnail-icon-assign">
                {userName ? userName[0] : "M"}
            </div>;
    }

    render = () => {

        const grid = this.props.view === 'grid';
        const stack = this.props.stack;
        const docs = this.props.docs ? (grid ? this.props.docs : this.sort(this.props.docs)) : [];
        let sel = this.props.selection ? this.props.selection : [];
        const allowDrop = ((stack && stack._id && !onPage("archives")) && !onPage('trash')) || (onPage("/field/") && this.props.match.params.field && this.props.match.params.value);

        return (<div
            className="files files-expand"
            onContextMenu={e => {
                this.setState({ cmRoot: true, isDocumentContextMenuOpen: true });
                this.openMenu(e, null);
            }}
            onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!allowDrop) return;
                this.setState({ dragOver: false });
                let files = [];
                if (e.dataTransfer.items)
                    for (let item of e.dataTransfer.items)
                        files.push(item.getAsFile());
                if (files.length > 0 && files.length <= 30) {
                    if (onPage("/field/"))
                        this.props.uploadFilesExec(files, null, [{
                            key: this.props.match.params.field,
                            value: this.props.match.params.value
                        }]);
                    else this.props.uploadFilesExec(files, stack._id);
                }
                if (files.length > 30)
                    toast.error('You can only upload 30 files at a time');
            }}
            onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                this.setState({ dragOver: allowDrop })
            }}
            onDragEnter={() => this.setState({ dragOver: allowDrop })}
            onDragLeave={() => this.setState({ dragOver: false })}
            onClick={() => {
                if (allowDrop && docs.length === 0)
                {
                    if(this.state.isDocumentContextMenuOpen)
                    {
                        this.setState({ isDocumentContextMenuOpen : false })
                        this.closeMenu();    
                    }                  
                    else 
                        this.props.uploadFiles();
                }               
            }}
        >

            <h1>My Documents</h1>

            {/* Only render drop section if we haven't reached the end of our page. 
                This prevents it from showing up if we've moved all the docs out */}
            { !this.state.loadingDocuments && !this.state.reachedResultLimit && !this.state.loadingMore ? <div className={"files-drop" + (allowDrop && docs.length === 0 ? " files-drop-always" : "") + (this.state.dragOver ? " files-drop-dragover" : "")}>
                {docs.length === 0 ? <>
                    {// Archives
                    onPage("archives") ? <div
                    className="search-no-results">
                        <br /><br />
                        You don't have any archived documents.<br /><br /><br />
                        <img
                            src="https://static.stax.ai/images/icons/archive.png"
                            alt="Archives"
                            style={{
                                width: 100,
                                marginBottom: 30,
                            }}
                        /><br />
                        You can archive old documents that you don't need<br />
                        everyday access to. You won't be able to search for<br />
                        archived documents through the Stax·ai search bar,<br />
                        but you can use the <strong>Advanced Search</strong> on the<br />
                        right to search through all documents.
                    </div> :
                    onPage("trash") ? <div
                    className="search-no-results">
                        <br /><br />
                        You don't have any deleted documents.<br /><br /><br />
                        <img
                            src="https://static.stax.ai/images/icons/trash.png"
                            alt="Deleted documents"
                            style={{
                                width: 100,
                                marginBottom: 30,
                            }}
                        /><br />
                        Deleted some documents by mistake? Don't worry!<br />
                        You'll be able to recover deleted files up to 30 days<br />
                        after they've been deleted.<br /><br />
                    </div> :
                    // Quick access stack
                    stack && stack._id && stack._id.length  !== 24 ? <div className="search-no-results">
                        There are no documents in this stack. 
                    </div> :
                    
                    // Stack with base stack
                    stack && stack.base ? <div className="search-no-results">
                        There are no documents in this stack.<br /><br /><br />
                        This stack is loaded from a pre-trained model.<br />
                        Stax will automatically sort similar documents here.
                    </div> :

                    // Stack without base stack
                    <div
                        className="files-upload-hint"
                    >
                        <div style={{
                            pointerEvents: 'none',
                            position: 'absolute',
                            top: 'calc(50% + 10px)',
                            left: 'calc(50% - 200px)',
                            minHeight: '100%',
                            width: 400
                        }}>
                            You do not have any documents in this stack. <br />
                        </div>
                    </div>}
                </> : ""}
            </div> : null }

            {grid ? '' : <div className='stack-list-header'>
                <div
                    className="stack-list-header-left stack-list-header-link"
                    title="Click to sort by key"
                    onClick={this.toggleHeader("name")}
                    style={{ paddingLeft: '20px' }}
                >{this.getSortArrow("name")}Document Name</div>
                <div className="stack-list-meta">
                    <div
                        className="stack-list-header-item stack-list-header-link"
                        title="Click to sort by key"
                        onClick={this.toggleHeader("source")}
                        style={{ width: '200px' }}
                    >{this.getSortArrow("source")}Source</div>
                    <div
                        className="stack-list-header-item stack-list-header-link"
                        title="Click to sort by key"
                        onClick={this.toggleHeader("receivedOn")}
                        style={{ width: '120px' }}
                    >{this.getSortArrow("receivedOn")}Received On</div>
                    {
                        onPage('trash')? <>
                        <div
                            className="stack-list-header-item stack-list-header-link"
                            title="Click to sort by key"
                            onClick={this.toggleHeader("deletedOn")}
                            style={{ width: '120px' }}
                        >{this.getSortArrow("deletedOn")}Deleted On</div>
                        </>: 
                        <>
                        <div
                            className="stack-list-header-item stack-list-header-link"
                            title="Click to sort by key"
                            onClick={this.toggleHeader("lastModified")}
                            style={{ width: '120px' }}
                        >{this.getSortArrow("lastModified")}Last Modified</div>
                        </>
                    }
                </div>
            </div>}

            
            {this.state.loadingDocuments ? 
                <div style={{display: 'flex', marginLeft: 30, marginTop: 20, gap: 10}}>
                    <Loading
                        type="spin"
                        color="var(--color-primary)"
                        height={32}
                        width={32}/>
                    Loading...
                </div>
                :
                docs.map((doc,idx) => <div
                key={idx}
                className={"document document-" + this.props.view + (sel.includes(doc._id) ? " document-" + this.props.view + "-selected" : "")}
                title={doc.name + "\nSource: " + doc.source + (doc.flagged ? "\n\nFLAGGED - something went wrong while processing this document.\nOpen the document and look at the history or jobs on the right side to learn more." : "")}
                // Click to select
                onClick={async (e) => {

                    // Control + Click adds to/removes from selection
                    if (e.ctrlKey) {
                        if (sel.includes(doc._id))
                            sel.splice(sel.indexOf(doc._id),1);

                        else
                            sel.push(doc._id);
                    }

                    // Shift + Click selects/unselects all up to this
                    else if (e.shiftKey) {

                        // Select up to
                        if (sel.length === 0)
                            sel.push(doc._id);

                        else {

                            const selIds = sel.map(docId => docs.findIndex(d => d._id === docId));

                            const thisIdx = docs.findIndex(d => d._id === doc._id);

                            const firstIdx = Math.min(...selIds);
                            const lastIdx = Math.max(...selIds);

                            if (thisIdx > lastIdx)
                                sel.push(
                                    ...Array.from(Array(thisIdx-lastIdx).keys())
                                            .map(jdx => docs[jdx+lastIdx+1]._id)
                                );

                            else if (thisIdx < firstIdx)
                                sel.push(
                                    ...Array.from(Array(firstIdx-thisIdx).keys())
                                            .map(jdx => docs[jdx+thisIdx]._id)
                                );

                            else
                                sel.push(doc._id);

                        }

                    }

                    // Regular click selects/unselects this document
                    else
                        sel = sel.length > 1 ? [doc._id] : sel.includes(doc._id) ? [] : [doc._id];

                    await this.props.select(sel);
                }}

                // Double click to open
                onDoubleClick={async () => {
                    if(onPage("trash"))
                        return

                    if (doc.job && doc.job.working)
                        return toast.info("The document is still processing, try again in a few seconds...");

                    // Ensure we set our document to reviewed when opening!
                    if (!doc.reviewed) {
                        await this.props.updateDocument(doc._id, {reviewed: true}, true, false);
                    }
                    await this.props.setDocument(doc);
                    this.props.history.push((this.props.match.params.field && this.props.match.params.value ? "/field/" + this.props.match.params.field + "/" +  this.props.match.params.value : '/stack/' + (this.props.match.params.stackId ? this.props.match.params.stackId : doc.stack ? doc.stack : "unclassified")) + '/document/' + doc._id + (onPage("archives") ? '?from=archives' : ""));
                }}

                // Drag to move
                draggable={true}
                onDragStart={async (e) => {

                    // Prevent default behavior (firefox drag issue)
                    e.preventDefault()
                    if(onPage('trash')) // Even if setting draggable to false, this will still be executed. 
                        return;
                    // Move multiple (selected) documents
                    if (sel.length > 1)
                        await this.props.moveDocuments(sel);

                    // Move just this document
                    else await this.props.moveDocuments([doc._id]);
                }}

                // Right click for context menu
                onContextMenu={e => {
                    this.setState({ cmRoot: false, isDocumentContextMenuOpen: true });
                    this.openMenu(e, doc);
                    
                }}
            >

                {doc.thumbnail ? <div className="document-thumbnail">
                    <img
                        className="document-thumbnail-data"
                        src={doc.thumbnail}
                        alt={doc.name}
                    />
                    {doc.job && this.renderJobStatus(doc)}
                    {doc.assignee && this.renderAssignStatus(doc)}
                </div> : <div
                    className={"document-thumbnail document-thumbnail-empty" + (doc.format && doc.format !== "doc" ? " document-thumbnail-nondoc" : "")}
                    title={doc.job && !doc.job.success && doc.job.error ? "Error processing document:\n" + doc.job.error : ""}
                >
                    {/* Format specific? */}
                    {doc.format && doc.format !== "doc" ? <img
                        className="document-thumbnail-format-data"
                        src={"https://static.stax.ai/images/format/" + doc.format + ".png"}
                        alt={doc.format}
                    /> : ""}
                    {doc.job && <>
                        {this.renderJobStatus(doc)}
                        {this.renderAssignStatus(doc)}

                        {/* Raw Extension/format */}
                        {grid && doc.job.upload && doc.job.upload.includes(".") && <div className="document-thumbnail-format">
                            {doc.job.upload.split(".").slice(-1).pop()}
                        </div>}
                    </>}
                </div>}

                {doc.urgent && <div className="urgent">URGENT</div>}

                {doc.flagged ? <img
                    className="document-flag"
                    src="https://static.stax.ai/images/icons/error.png"
                    alt="Flagged"
                /> : doc.pinned ? <img
                    className="document-flag"
                    src="https://static.stax.ai/images/icons/favorite.png"
                    alt="Pinned"
                /> : !doc.reviewed ? <div className="document-unread" /> : ""}

                <h1>{doc.name} {doc.pages.length ? <span style={{ color: "rgb(180,180,180)" }}>({doc.pages.length} {plural("page", doc.pages.length)})</span> : ""}</h1>

                {grid ? '' : <div className="stack-list-meta">
                    <div className="stack-list-meta-col document-list-meta-col"
                        style={{ width: '200px' }}
                    >
                        <img
                            src={getSourceIcon(doc.source)}
                            alt={doc.source}
                        />
                        {doc.source.split(": ").slice(-1)[0]}
                    </div>
                    <div className="stack-list-meta-col document-list-meta-col"
                        style={{ width: '120px' }}
                    >{formatDate(doc.receivedOn)}</div>
                    <div className="stack-list-meta-col document-list-meta-col"
                        style={{ width: '124px' }}
                    >{formatDate(onPage('trash')? doc.deletedOn: doc.lastModified)}</div>
                </div>}

            </div>)}

            <div style={{ clear: 'both' }} />

            {this.state.loadingMore ? 
            <div className="search-loading">
                <Loading
                    type="bubbles"
                    color="var(--color-primary)"
                    height={100}
                    width={100}
                />
            </div> : <div></div>} 

            {/* Document context menu control */}
            <ContextMenu
                load={(open,close) => {
                    this.openMenu = open;
                    this.closeMenu = close;
                }}
                options={this.getContextMenuOptions()}
            />

        </div>);
    }

}